import { makeAutoObservable, runInAction } from 'mobx';
import { customFetch } from '@/shared/api';

const NEXT_PUBLIC_API_URL_CLIENT_SIDE =
  process.env.NEXT_PUBLIC_API_URL_CLIENT_SIDE;

class AvatarStore {
  // Observable state
  avatarUrl: string | null = null;
  loading: boolean = false;
  error: string | null = null;
  optimisticUrl: string | null = null; // For instant updates
  currentAvatarPath: string | null = null; // Track current path
  displayName: string | null = null; // User's display name from API

  constructor() {
    makeAutoObservable(this);
  }

  setDisplayName(name: string | null) {
    this.displayName = name;
  }

  // Computed - final URL for display
  get finalAvatarUrl() {
    return this.optimisticUrl || this.avatarUrl;
  }

  // Actions
  setAvatarUrl(url: string | null) {
    this.avatarUrl = url;
  }

  setLoading(loading: boolean) {
    this.loading = loading;
  }

  setError(error: string | null) {
    this.error = error;
  }

  setOptimisticUrl(url: string | null) {
    this.optimisticUrl = url;
    console.log('🎯 Set optimistic URL:', url);
  }

  clearOptimistic() {
    if (this.optimisticUrl) {
      console.log('🧹 Clearing optimistic URL:', this.optimisticUrl);
      this.optimisticUrl = null;
    }
  }

  reset() {
    console.log('🔄 Resetting avatar store');
    this.avatarUrl = null;
    this.loading = false;
    this.error = null;
    this.optimisticUrl = null;
    this.currentAvatarPath = null;
    this.displayName = null;
  }

  async loadAvatar(avatarPath: string, retries = 3): Promise<void> {
    this.loading = true;
    this.error = null;
    let attempts = 0;
    const maxAttempts = retries + 1; // Initial attempt + retries

    while (attempts < maxAttempts) {
      try {
        const response = await customFetch(
          `${NEXT_PUBLIC_API_URL_CLIENT_SIDE}/avatar`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ path: avatarPath }),
          }
        );

        if (!response.ok) {
          const text = await response.text();
          const errorMsg = `Failed to fetch avatar: ${response.status} - ${text}`;

          attempts++;
          if (attempts < maxAttempts) {
            console.log(`⚠️ Attempt ${attempts} failed, retrying...`);
            await new Promise((res) => setTimeout(res, 700));
            continue;
          }

          console.error('❌ Avatar API error:', errorMsg);
          runInAction(() => {
            this.avatarUrl = null;
            this.error = errorMsg;
            this.loading = false;
          });
          return;
        }

        const data = await response.json();
        console.log('📦 Avatar signed URL received:', data.avatarUrl);

        runInAction(() => {
          this.avatarUrl = data.avatarUrl;
          this.loading = false;
          this.error = null;
        });
        return;
      } catch (e) {
        attempts++;
        if (attempts < maxAttempts) {
          console.log(`⚠️ Attempt ${attempts} failed with error, retrying...`);
          await new Promise((res) => setTimeout(res, 700));
          continue;
        }

        console.error('❌ Avatar fetch error:', e);
        runInAction(() => {
          this.avatarUrl = null;
          this.error = e instanceof Error ? e.message : 'Unknown error';
          this.loading = false;
        });
        return;
      }
    }
  }

  // Forced reload
  async forceReload(avatarPath: string) {
    console.log('🔄 Force reloading avatar for:', avatarPath);

    runInAction(() => {
      this.avatarUrl = null;
      this.currentAvatarPath = null;
      this.optimisticUrl = null;
    });

    // Reload
    await this.loadAvatar(avatarPath);
  }

  // Update avatar path (called when session changes)
  updateAvatarPath(newPath: string | null) {
    if (this.currentAvatarPath === newPath) {
      // If avatarUrl is missing but path is the same — load!
      if (!this.avatarUrl && newPath) {
        this.loadAvatar(newPath);
      }
      // else skip
      return;
    }
    // If path changed — reset and load new
    this.currentAvatarPath = newPath;
    this.avatarUrl = null;
    if (newPath) {
      this.loadAvatar(newPath);
    }
  }
}

// Create singleton instance
export const avatarStore = new AvatarStore();
